// ============================================================================
// MASTER COLOUR LIST — single source of truth for all block colour pickers
// Format: ['value', 'Label', 'lightness']
//   value     → CSS variable or hex (used as the stored value)
//   Label     → What editors see in the dropdown
//   lightness → 'light' or 'dark' (used to determine foreground/text colour)
//
// Import in block schemas and components:
//   import { MASTER_COLOR_LIST, getColorChoices, isColorDark } from '../../config/colors';
//
// To extend with block-specific colours:
//   const blockColorList = [...MASTER_COLOR_LIST, ['transparent', 'Transparent', 'light']];
//   choices: getColorChoices(blockColorList)
// ============================================================================

export const MASTER_COLOR_LIST = [
  // ── Neutrals ──────────────────────────────────────────────────────────────
  ['#ffffff', 'White', 'light'],

  // ── Foundation palette ────────────────────────────────────────────────────
  ['var(--fsda-deep-space)', 'Deep Space', 'dark'],
  ['var(--fsda-upper-atmosphere)', 'Upper Atmosphere', 'dark'],
  ['var(--fsda-meteorite-surface)', 'Meteorite Surface', 'dark'],
  ['var(--fsda-lunar-regolith)', 'Lunar Regolith', 'dark'],
  ['var(--fsda-starlight)', 'Starlight', 'light'],

  // ── Deep Void (main CTA) ──────────────────────────────────────────────────
  ['var(--fsda-deep-void-base)', 'Deep Void', 'dark'],
  ['var(--fsda-deep-void-dark)', 'Deep Void — Dark', 'dark'],

  // ── Africa2Moon — Lunar Blue ──────────────────────────────────────────────
  ['var(--fsda-lunar-blue-base)', 'Lunar Blue', 'dark'],
  ['var(--fsda-lunar-blue-dark)', 'Lunar Blue — Dark', 'dark'],

  // ── Africa First — Ember Gold ─────────────────────────────────────────────
  ['var(--fsda-ember-gold-base)', 'Ember Gold', 'dark'],
  ['var(--fsda-ember-gold-dark)', 'Ember Gold — Dark', 'dark'],

  // ── Data for Disaster Management — Alert Crimson ──────────────────────────
  ['var(--fsda-alert-crimson-base)', 'Alert Crimson', 'dark'],
  ['var(--fsda-alert-crimson-dark)', 'Alert Crimson — Dark', 'dark'],

  // ── Geomagnetic Solar Climate Model — Plasma Pink ─────────────────────────
  ['var(--fsda-plasma-pink-base)', 'Plasma Pink', 'dark'],
  ['var(--fsda-plasma-pink-dark)', 'Plasma Pink — Dark', 'dark'],

  // ── African Astronaut Programme — Comet Cyan ──────────────────────────────
  ['var(--fsda-comet-cyan-base)', 'Comet Cyan', 'dark'],
  ['var(--fsda-comet-cyan-dark)', 'Comet Cyan — Dark', 'dark'],

  // ── Space Resources — Meteor Strike ───────────────────────────────────────
  ['var(--fsda-meteor-strike-base)', 'Meteor Strike', 'dark'],
  ['var(--fsda-meteor-strike-dark)', 'Meteor Strike — Dark', 'dark'],

  // ── Asteroid Mining — Cobalt Deep ─────────────────────────────────────────
  ['var(--fsda-cobalt-deep-base)', 'Cobalt Deep', 'dark'],
  ['var(--fsda-cobalt-deep-dark)', 'Cobalt Deep — Dark', 'dark'],

  // ── Education & Skills Training — Aurora Violet ───────────────────────────
  ['var(--fsda-aurora-violet-base)', 'Aurora Violet', 'dark'],
  ['var(--fsda-aurora-violet-dark)', 'Aurora Violet — Dark', 'dark'],

  // ── Economic Modelling Framework — Steel Teal ─────────────────────────────
  ['var(--fsda-steel-teal-base)', 'Steel Teal', 'dark'],
  ['var(--fsda-steel-teal-dark)', 'Steel Teal — Dark', 'dark'],

  // ── Solar Flare (reserved) ────────────────────────────────────────────────
  ['var(--fsda-solar-flare-base)', 'Solar Flare', 'dark'],
  ['var(--fsda-solar-flare-dark)', 'Solar Flare — Dark', 'dark'],
];

// Strip lightness — returns [value, label] pairs for Volto select widgets
export const getColorChoices = (list = MASTER_COLOR_LIST) =>
  list.map(([value, label]) => [value, label]);

// Returns true if the colour is dark (i.e. needs light/white text over it)
export const isColorDark = (value, list = MASTER_COLOR_LIST) => {
  const match = list.find(([v]) => v === value);
  return match ? match[2] === 'dark' : false;
};

// Convert a CSS variable value back to a safe key for use in class names / storage
// e.g. 'var(--fsda-lunar-blue-base)' → 'fsda-lunar-blue-base'
//      '#ffffff'                      → 'ffffff'
export const colorValueToKey = (value) =>
  value
    .replace(/var\(--/, '')
    .replace(/\)/, '')
    .replace(/^#/, '')
    .replace(/[^a-z0-9-]+/gi, '-')
    .toLowerCase();

// Reverse: given a key, find its original CSS variable / hex value from the list
export const colorKeyToCssVar = (key, colorList = MASTER_COLOR_LIST) => {
  const match = colorList.find(([value]) => colorValueToKey(value) === key);
  return match ? match[0] : key; // fallback: return the key as-is
};

// Generate solid + outlined button choices from the master colour list.
// Format: ['solid__fsda-lunar-blue-base', 'Lunar Blue — Solid']
// The double-underscore separator avoids collision with CSS variable dashes.
export const getButtonChoices = (colorList = MASTER_COLOR_LIST) =>
  colorList.flatMap(([value, label, lightness]) => {
    const key = colorValueToKey(value);
    return [
      [`solid__${key}`, `${label} — Solid`, lightness],
      [`outline__${key}`, `${label} — Outlined`, lightness],
    ];
  });

// Return the most appropriate default button value for a given background colour.
// On a dark background → suggests an outlined or solid white/light colour.
// On a light background → suggests an outlined or solid dark/primary colour.
export const getDefaultButton = (
  bgColorValue,
  colorList = MASTER_COLOR_LIST,
) => {
  const bgEntry = colorList.find(([value]) => value === bgColorValue);
  const bgLightness = bgEntry?.[2] ?? 'light';

  // For a dark background, find the first *light* colour (e.g. White)
  // For a light background, find the first *dark* colour (e.g. Deep Space)
  const targetLightness = bgLightness === 'dark' ? 'light' : 'dark';
  const preferredColor = colorList.find(([, , l]) => l === targetLightness);
  const fallback = colorList[0];

  const key = colorValueToKey((preferredColor ?? fallback)[0]);
  return `outline__${key}`;
};

// Given a button style value (e.g. 'solid__fsda-lunar-blue-base') and a base class,
// returns a className string and an inline style object with --btn-color set.
//
// The className always includes:
//   - The base class (e.g. 'button', 'embla__slide-btn')
//   - 'btn-unified'              → marks it as a unified system button (target all at once)
//   - 'btn-{variant}'            → 'btn-solid' or 'btn-outline'
//   - 'btn-color-{key}'          → e.g. 'btn-color-fsda-lunar-blue-base'
//   - '{baseClass}--{variant}'   → e.g. 'button--solid' (for local block CSS)
//
// Usage:
//   import { getButtonClasses } from '../../../config/colors';
//   const { className, style } = getButtonClasses(item.buttonStyle, 'button');
//
// CSS targeting examples:
//   .btn-unified                          { /* all system buttons, any block */ }
//   .btn-solid                            { /* all solid buttons, any block */ }
//   .btn-outline                          { /* all outlined buttons, any block */ }
//   .btn-color-fsda-lunar-blue-base       { /* lunar blue buttons across all blocks */ }
export const getButtonClasses = (value = '', baseClass = 'button') => {
  if (value && value.includes('__')) {
    const [variant, colorKey] = value.split('__');
    const cssVar = colorKeyToCssVar(colorKey);
    return {
      className: [
        baseClass,
        'btn-unified',
        `btn-${variant}`,
        `btn-color-${colorKey}`,
        `${baseClass}--${variant}`,
      ].join(' '),
      style: { '--btn-color': cssVar },
    };
  }
  // Non-standard value — pass through untouched
  return { className: `${baseClass} ${value}`.trim(), style: {} };
};
